import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Swords, Handshake, ArrowRight } from 'lucide-react';

export default function NegotiationSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section data-testid="negotiation-section" className="relative py-24 md:py-32 overflow-hidden"
      style={{ background: 'var(--color-surface)' }}
    >
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-1/2 h-full"
          style={{ background: 'radial-gradient(ellipse at top right, var(--color-glow), transparent 70%)' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Trial Lawyers */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))', color: '#fff' }}
              >
                <Swords size={22} />
              </div>
              <h2 data-testid="trial-lawyers-heading" className="font-heading font-bold text-3xl tracking-tight" style={{ color: 'var(--color-text)' }}>
                Top Performance<br />Trial Lawyers
              </h2>
            </div>
            <p className="font-body text-base leading-relaxed mb-4" style={{ color: 'var(--color-text-muted)' }}>
              Preparing for litigation is a lengthy process that can be frustrating when you
              don&apos;t know what to expect or are not well-equipped for the proceedings. One
              unfavorable ruling can have a significant and potentially devastating effect on
              your goals, business plans, freedom and financial standings.
            </p>
            <p className="font-body text-base leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
              Our aggressive performance in the courtroom matches our commitment to due
              diligence, thorough case investigation and presentation. We are fearless in the
              face of our opponents, unafraid to go after the compensation and fair ruling you
              deserve. Our rate of success positions us among the top law partners in the state.
            </p>
          </motion.div>

          {/* Negotiation */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))', color: '#fff' }}
              >
                <Handshake size={22} />
              </div>
              <h2 data-testid="negotiation-heading" className="font-heading font-bold text-3xl tracking-tight" style={{ color: 'var(--color-text)' }}>
                Negotiation &<br />Dispute Resolution
              </h2>
            </div>
            <p className="font-body text-base leading-relaxed mb-4" style={{ color: 'var(--color-text-muted)' }}>
              Drawn-out negotiations or litigation involving contracts, disputes or any other
              area of the law become expensive and counterproductive to the business at hand
              very quickly. When your focus and resources shift to defending your interests and
              protecting your rights, a bad situation can turn to a worse one very quickly.
            </p>
            <p className="font-body text-base leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
              When your transaction requires the refined skills of an expert team of negotiation
              and dispute solution innovators, we are here to lead the way through bureaucratic
              red tape, complicated legislation and aggressive opposition. Our mission is to
              pave the way to your goals through sound logic and reliable legal advice.
            </p>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <button
            data-testid="negotiation-cta"
            onClick={() => scrollTo('contact')}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-md text-white font-semibold transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:scale-105"
            style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))' }}
          >
            Schedule a Consultation
            <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
